import React, { memo, useId, useState } from "react";
import { SideDrawer, SideDrawerCloseButton } from "@dmc/ui";
import ApiData from "../../api/ApiData";
import Msg from "../../api/Msg";
import { observer } from "mobx-react-lite";
import { InputEditVer1 } from "../UI/InputEditVer1";
import RolesState from "../../api/RolesState";
import { RemoveBtnModal } from "../UI/button/RemoveBtnModal";
import { BtnPrimaryv4 } from "../UI/button/BtnPrimaryv4";
import { SiteModalToolbarBtn } from "./SiteModalToolbarBtn";
import ProductionYandexMap from "./ProductionYandexMap";
import { SiteCoordinatsField } from "./SiteCoordinatsField";
import { BtnOutlineV4 } from "../UI/button/BtnOutlineV4";
import { SiteFormField } from "../UI/SiteFormField";
import { PRODUCTION_FORM_FIELDS } from "../../utils/productionFormFields";
import {
  COORDINATS_FORMAT_MESSAGE,
  formatCoordinatsForApi,
  formatOptionalProductionField,
  isValidCoordinatsInput,
  parseCoordinats,
  PRODUCTION_NAME_REQUIRED_MESSAGE,
} from "../../utils/productionCoordinats";
import { useOptionalCompanyPrintLabelStatsContext } from "./card/CompanyPrintLabelStatsContext";
import { formatEventTotalDisplay } from "../../utils/eventTotalsSaUtils";

const formatDetail = (value) =>
  value && String(value).trim() !== "" ? value : null;

function SitePrintLabelSkeleton() {
  return (
    <div
      className="site-print-label-skeleton"
      aria-busy="true"
      aria-label="Загрузка статистики печати"
    >
      <div className="event-totals-skeleton-chip site-print-label-skeleton-line" />
      <div className="event-totals-skeleton-chip site-print-label-skeleton-line" />
      <div className="event-totals-skeleton-chip site-print-label-skeleton-line site-print-label-skeleton-line--short" />
    </div>
  );
}

const ThisProduction = observer(({ setFlagThisModalProduction }) => {
  const titleId = useId();
  const printStats = useOptionalCompanyPrintLabelStatsContext();
  const [flagEdit, setFlagEdit] = useState(false);
  const [nameProduction, setNameProduction] = useState(
    ApiData.company.this_production.name_production,
  );
  const [productionAddress, setProductionAddress] = useState(
    ApiData.company.this_production.address_production,
  );
  const [contactRepresentative, setContactRepresentative] = useState(
    ApiData.company.this_production.contact_representative || "",
  );
  const [phone, setPhone] = useState(
    ApiData.company.this_production.phone || "",
  );
  const [coordinats, setCoordinats] = useState(
    ApiData.company.this_production.coordinats || "",
  );

  const setters = {
    name: setNameProduction,
    address: setProductionAddress,
    contact: setContactRepresentative,
    phone: setPhone,
  };

  const values = {
    name: nameProduction,
    address: productionAddress,
    contact: contactRepresentative,
    phone: phone,
  };

  const parsedCoordinats = parseCoordinats(coordinats);

  const productionId = ApiData.company.this_production?.id;
  const showPrintStats = Boolean(printStats?.canView) && !flagEdit;
  const printLoading = printStats?.loading;
  const printError = printStats?.error;
  const printLabels =
    showPrintStats && !printLoading && !printError && printStats
      ? printStats.getLabelsForProduction(productionId)
      : [];

  const updateProduction = async () => {
    const nameTrimmed = String(nameProduction).trim();
    if (nameTrimmed === "") {
      return Msg.writeMessages(PRODUCTION_NAME_REQUIRED_MESSAGE);
    }

    if (!isValidCoordinatsInput(coordinats)) {
      return Msg.writeMessages(COORDINATS_FORMAT_MESSAGE);
    }

    const dataProduction = {
      company_id: Number(ApiData.company.this_production.company_id),
      name_production: nameTrimmed,
      address_production: formatOptionalProductionField(productionAddress),
      contact_representative: formatOptionalProductionField(
        contactRepresentative,
      ),
      phone: formatOptionalProductionField(phone),
      coordinats: formatCoordinatsForApi(coordinats),
    };
    const res = await ApiData.updateProduction(dataProduction);
    if (res) {
      setFlagEdit(false);
    }
  };

  const close = () => setFlagThisModalProduction(false);

  const renderDetailLine = (label, value, mono = false) => {
    const formatted = formatDetail(value);
    return (
      <div className="site-detail-line">
        <span className="detail-label">{label}:</span>
        <span
          className={
            formatted
              ? `detail-value${mono ? " site-detail-mono" : ""}`
              : "detail-value detail-value-muted"
          }
        >
          {formatted || "не указано"}
        </span>
      </div>
    );
  };

  return (
    <SideDrawer
      open
      onClose={close}
      title="Площадка"
      titleId={titleId}
      usePortal={false}
      toolbar={
        <>
          {RolesState.patch_production && !flagEdit ? (
            <>
              <RemoveBtnModal
                setClosed={setFlagThisModalProduction}
                toolbarV4
              />
              <SiteModalToolbarBtn
                variant="edit"
                onClick={() => setFlagEdit(true)}
                title="Редактировать"
              />
            </>
          ) : null}
          <SideDrawerCloseButton onClick={close} title="Закрыть" />
        </>
      }
      footer={
        flagEdit ? (
          <>
            <BtnPrimaryv4 onClick={updateProduction} text="Сохранить" />
            <BtnOutlineV4 onClick={() => setFlagEdit(false)} text="Отменить" />
          </>
        ) : null
      }
    >
        <div className="site-modal-body">
          {!flagEdit ? (
            <div className="site-modal-view">
              <p className="site-modal-site-name">{nameProduction}</p>
              {renderDetailLine("Адрес", productionAddress)}
              {renderDetailLine("ФИО", contactRepresentative)}
              {renderDetailLine("Телефон", phone, true)}
              {showPrintStats ? (
                <div className="site-print-label-stats site-modal-print-label-stats">
                  <div className="site-detail-line site-print-label-stats-heading">
                    <span className="detail-label">Статистика печати за 30 дней:</span>
                  </div>
                  {printLoading ? (
                    <SitePrintLabelSkeleton />
                  ) : printError ? null : printLabels.length === 0 ? (
                    <p className="event-totals-muted site-print-label-empty">
                      Нет печати за период
                    </p>
                  ) : (
                    printLabels.map((row) => (
                      <div
                        key={row.key}
                        className="site-detail-line site-print-label-line"
                      >
                        <span className="detail-label site-print-label-size">
                          {row.display}
                        </span>
                        <span className="detail-value">
                          {formatEventTotalDisplay(row.count)} шт
                        </span>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
              <div className="site-map-block">
                {parsedCoordinats ? (
                  <ProductionYandexMap
                    lat={parsedCoordinats.lat}
                    lon={parsedCoordinats.lon}
                    nameProduction={nameProduction}
                  />
                ) : (
                  <p className="site-map-empty">
                    Карта не настроена, включите режим редактирования
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="site-modal-fields">
              {PRODUCTION_FORM_FIELDS.map((field) => (
                <SiteFormField key={field.id} label={field.label}>
                  <InputEditVer1
                    className="site-form-control"
                    value={values[field.id]}
                    onChange={(e) => setters[field.id](e.target.value)}
                    placeholder={field.placeholder}
                  />
                </SiteFormField>
              ))}
              <SiteCoordinatsField
                value={coordinats}
                onChange={(e) => setCoordinats(e.target.value)}
              />
            </div>
          )}
        </div>
    </SideDrawer>
  );
});

export default memo(ThisProduction);
